import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/GeneralComponents/Button";
import DomeCareTitle from "../../components/GeneralComponents/DomeCareTitle";
import ErrorsWrapper from "../../components/GeneralComponents/ErrorsWrapper";
import { useAuthContext } from "../../context/AuthContext";
import history from "../../hooks/History";
import useToast from "../../hooks/useToast";
import {
  getRegisterUserOTPInfo,
  setCreatePasswordType,
  setIsOtpValidate,
  setOtpReducer,
  setPasswordReducer,
  updateOTPDuration,
} from "../../redux/slices/authSlice";
import { RootState } from "../../redux/store";
import { formatTime, maskPhoneNumber } from "../../utils/Functions";
import { useForgetPasswordCheckCode } from "../../utils/api/AuthAPIs/useForgetPasswordCheckCode";
import { useForgetPasswordResendOTP } from "../../utils/api/AuthAPIs/useForgetPasswordResendOTPCode";
import { useResendOTP } from "../../utils/api/AuthAPIs/useResendOTPCode";
import { useVerifyOTP } from "../../utils/api/AuthAPIs/useVerifyNumber";
import { useProfileChangePhoneNumberResendOTPCode } from "../../utils/api/ProfileAPIs/useProfileChangePhoneNumberResendOTP";
import { useProfileChangePhoneNumberVerify } from "../../utils/api/ProfileAPIs/useProfileChangePhoneNumberVerify";
import {
  BASE_LOCAL_IMAGES_DIR,
  DefaultNavigateDelay,
  GlobalToasterDuration,
  ToasterErrorImageUrl,
  ToasterSuccessImageUrl,
} from "../../utils/constants/CommonConstants";
import {
  otp_IMG,
  sms_white_IMG,
  whatsapp_IMG,
} from "../../utils/constants/Images";
interface VerifyOtpProps {
  OriginType: string;
}

const VerifyOtp: React.FC<VerifyOtpProps> = ({ OriginType }) => {
  const { t } = useTranslation("translation");
  const { changeShowLeaveAuthRoutePopupAuthState } = useAuthContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { userId, otpDuration, phoneNumber, otpType, user, countryData } =
    useSelector((state: RootState) => state.auth);
  const { ProfileUserInfo } = useSelector((state: RootState) => state.profile);

  const verifyBtnRef = useRef<HTMLButtonElement>(null);
  //?===========================States============================
  const [otp, setOtp] = useState<string>("");
  const [countdown, setCountdown] = useState<any>(
    localStorage.getItem("otpDuration"),
  );
  const [showMessageOptions, setShowMessageOptions] = useState<boolean>(false);
  const [isValidOTP, setIsValidOTP] = useState<boolean | null>(null);
  //?============ Separate loading states=============
  const [loadingWhatsApp, setLoadingWhatsApp] = useState<boolean>(false);
  const [loadingSMS, setLoadingSMS] = useState<boolean>(false);
  //?============POST Verify OTP Form=============
  const {
    error: verifyOtpError,
    mutate: VerifyOTPMutate,
    status: VerifyOTPStatus,
    reset: VerifyOTPReset,
  } = useVerifyOTP();

  const {
    error: ForgetPasswordCheckCodeError,
    mutate: ForgetPasswordCheckCodeMutate,
    reset: ForgetPasswordCheckCodeReset,
  } = useForgetPasswordCheckCode();

  const {
    error: ProfileChangePhoneNumberVerifyError,
    mutate: ProfileChangePhoneNumberVerifyMutate,
    reset: ProfileChangePhoneNumberVerifyReset,
  } = useProfileChangePhoneNumberVerify();

  //?============POST Resend OTP Form=============
  const {
    error: resendOTPError,
    mutate: ResendOTPMutate,
    reset: ResendOTPReset,
  } = useResendOTP();
  const {
    error: resendForgetPasswordError,
    mutate: ResendForgetPasswordMutate,
    reset: ForgetPasswordResendOTPReset,
  } = useForgetPasswordResendOTP();

  const {
    error: ProfileChangePhoneNumberResendOTPCodeError,
    mutate: ProfileChangePhoneNumberResendOTPCodeMutate,
    reset: ProfileChangePhoneNumberResendOTPCodeReset,
  } = useProfileChangePhoneNumberResendOTPCode();

  //?=============== Initialize countdown from localStorage or otpDuration=============
  useEffect(() => {
    const storedCountdown = localStorage.getItem("otpDuration");
    const initialCountdown = storedCountdown
      ? JSON.parse(storedCountdown)
      : otpDuration || 0;
    setCountdown(initialCountdown);
  }, [otpDuration]);

  //?============ Resend Time interval and Store countdown in localStorage ===========
  useEffect(() => {
    if (countdown === 0) return;
    const interval = setInterval(() => {
      setCountdown((prev: any) => {
        const newCountdown = prev > 0 ? prev - 1 : 0;
        localStorage.setItem("otpDuration", JSON.stringify(newCountdown));
        dispatch(updateOTPDuration(newCountdown));
        return newCountdown;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  //?== Store OTP Duration in localStorage whenever it changes
  useEffect(() => {
    if (otpDuration !== undefined) {
      localStorage.setItem("otpDuration", JSON.stringify(otpDuration));
    }
  }, [otpDuration]);

  //?=================Mask Phone Number=================

  //? Fallback for null or undefined phoneNumber
  const rawPhoneNumber = ProfileUserInfo.phoneNumber || phoneNumber;

  const displayedPhoneNumber = rawPhoneNumber
    ? maskPhoneNumber(rawPhoneNumber, countryData?.ZipCode || "+963")
    : t("phone_number_not_available");

  //?============Verification OTP Function=============
  const handleVerifyOtpCode = async () => {
    if (!otp) {
      showToast(
        t("verify_otp"),
        t("verify_otp_existance_error"),
        "error",
        GlobalToasterDuration,
        "bottom-right",
        ToasterErrorImageUrl,
      );
      return;
    }

    const showSuccessToast = () => {
      showToast(
        t("verify_otp"),
        t("success_to_verify_otp"),
        "success",
        GlobalToasterDuration,
        "bottom-right",
        ToasterSuccessImageUrl,
      );
    };

    const showErrorToast = () => {
      showToast(
        t("verify_otp"),
        t("failed_to_verify_otp"),
        "error",
        GlobalToasterDuration,
        "bottom-right",
        ToasterErrorImageUrl,
      );
    };

    const commonSuccessActions = (type: string) => {
      setIsValidOTP(true);
      dispatch(setIsOtpValidate(true));
      dispatch(setCreatePasswordType(type));
      dispatch(setOtpReducer(otp));
      showSuccessToast();
      localStorage.removeItem("otpDuration");
      dispatch(updateOTPDuration(0));
      changeShowLeaveAuthRoutePopupAuthState(false);
    };
    switch (otpType) {
      case "register":
        VerifyOTPMutate(
          {
            Id: userId,
            VerificationCode: otp,
          },
          {
            onSuccess: (response) => {
              if (response.Status) {
                commonSuccessActions("register");

                setTimeout(() => {
                  history.push("/auth/create-password");
                }, DefaultNavigateDelay);
              }
            },
            onError: () => {
              showErrorToast();
              setIsValidOTP(false);
            },
          },
        );
        break;
      case "forgotPassword":
        if (OriginType === "account") {
          ForgetPasswordCheckCodeMutate(
            {
              Id: userId,
              VerificationCode: otp,
            },
            {
              onSuccess: (response) => {
                if (response.Status) {
                  commonSuccessActions("forgotPassword");

                  setTimeout(() => {
                    history.push("/account/forget-password/create-password");
                  }, DefaultNavigateDelay);
                }
              },
              onError: () => {
                showErrorToast();
                setIsValidOTP(false);
              },
            },
          );
        } else {
          ForgetPasswordCheckCodeMutate(
            {
              Id: userId,
              VerificationCode: otp,
            },
            {
              onSuccess: (response) => {
                if (response.Status) {
                  commonSuccessActions("forgotPassword");
                  setTimeout(() => {
                    history.push("/auth/create-password");
                  }, DefaultNavigateDelay);
                }
              },
              onError: () => {
                showErrorToast();
                setIsValidOTP(false);
              },
            },
          );
        }

        break;
      case "new-number":
        if (OriginType === "account") {
          ProfileChangePhoneNumberVerifyMutate(
            {
              payload: {
                VerificationCode: otp,
              },
              token: user?.token,
            },
            {
              onSuccess: (response) => {
                if (response.Status) {
                  commonSuccessActions("new-number");

                  dispatch(
                    getRegisterUserOTPInfo({ userId: ProfileUserInfo.Id }),
                  );
                  dispatch(setPasswordReducer({ password: "" }));

                  setTimeout(() => {
                    history.push("/");
                  }, DefaultNavigateDelay);
                }
              },
              onError: () => {
                showErrorToast();
                setIsValidOTP(false);
              },
            },
          );
        }
        break;
      case "VerifyStaffOTP":
        VerifyOTPMutate(
          {
            Id: userId,
            VerificationCode: otp,
          },
          {
            onSuccess: (response) => {
              if (response.Status) {
                commonSuccessActions("NewStaff");

                setTimeout(() => {
                  history.push("/auth/congrats");
                }, DefaultNavigateDelay);
              }
            },
            onError: () => {
              showErrorToast();
              setIsValidOTP(false);
            },
          },
        );
        break;
      default:
        showToast(
          t("verify_otp"),
          t("invalid_otp_type_error"),
          "error",
          GlobalToasterDuration,
          "bottom-right",
          ToasterErrorImageUrl,
        );
        setIsValidOTP(false);
        break;
    }
  };
  //?============Resend OTP Function=============
  const handleResendOtpCode = async (type: number) => {
    // if (type === 0) setLoadingWhatsApp(true);
    // if (type === 1) setLoadingSMS(true);

    // if (type === 0) setLoadingSMS(true);
    // if (type === 1) setLoadingWhatsApp(true);
    setOtp("");
    setIsValidOTP(null);
    setShowMessageOptions(false);
    if (otpType === "register") {
      ResendOTPMutate(
        {
          Id: userId,
          Type: type,
        },
        {
          onSuccess: (data) => {
            if (data.Status) {
              setCountdown(data?.Data?.Duration);
              localStorage.removeItem("otpDuration");
              dispatch(updateOTPDuration(0));
              showToast(
                t("resend_otp"),
                t("success_to_resend_otp"),
                "success",
                GlobalToasterDuration,
                "bottom-right",
                ToasterSuccessImageUrl,
              );
            }
          },
          onError: () => {
            showToast(
              t("resend_otp"),
              t("failed_to_resend_otp"),
              "error",
              GlobalToasterDuration,
              "bottom-right",
              ToasterErrorImageUrl,
            );
            setIsValidOTP(false);
            ResendOTPReset();
          },
        },
      );
    } else if (otpType === "forgotPassword") {
      ResendForgetPasswordMutate(
        {
          Id: userId,
          Type: type,
        },
        {
          onSuccess: (data) => {
            if (data.Status) {
              localStorage.removeItem("otpDuration");
              // setShowLeaveDialog(false);
              setCountdown(data?.Data?.Duration);
              dispatch(updateOTPDuration(0));
              showToast(
                t("resend_otp"),
                t("success_to_resend_otp"),
                "success",
                GlobalToasterDuration,
                "bottom-right",
                ToasterSuccessImageUrl,
              );
            }
          },
          onError: (data) => {
            showToast(
              t("resend_otp"),
              t("failed_to_resend_otp"),
              "error",
              GlobalToasterDuration,
              "bottom-right",
              ToasterErrorImageUrl,
            );
            setIsValidOTP(false);
            ResendOTPReset();
          },
        },
      );
    } else if (otpType === "new-number") {
      ProfileChangePhoneNumberResendOTPCodeMutate(
        {
          payload: {
            Type: type,
          },
          token: user?.token,
        },
        {
          onSuccess: (data) => {
            if (data.Status) {
              setCountdown(data?.Data?.Duration);
              dispatch(updateOTPDuration(0));
              localStorage.removeItem("otpDuration");
              showToast(
                t("resend_otp"),
                t("success_to_resend_otp"),
                "success",
                GlobalToasterDuration,
                "bottom-right",
                ToasterSuccessImageUrl,
              );
            }
          },
          onError: (data) => {
            showToast(
              t("resend_otp"),
              t("failed_to_resend_otp"),
              "error",
              GlobalToasterDuration,
              "bottom-right",
              ToasterErrorImageUrl,
            );
            setIsValidOTP(false);
            ResendOTPReset();
          },
        },
      );
    }
    if (type === 0) setLoadingWhatsApp(false);
    if (type === 1) setLoadingSMS(false);
  };
  const handleShowMessageOptions = () => {
    setOtp("");
    setShowMessageOptions(true);
    ForgetPasswordResendOTPReset();
    ForgetPasswordCheckCodeReset();
    ProfileChangePhoneNumberResendOTPCodeReset();
    ProfileChangePhoneNumberVerifyReset();
    VerifyOTPReset();
    ResendOTPReset();
    dispatch(updateOTPDuration(0));
    setIsValidOTP(null);
  };
  //?============Handle Otp Input=============
  const handleOTPInput = (OTP: string) => {
    if (/^\d*$/.test(OTP) && OTP.length <= 6) {
      setOtp(OTP);
      setIsValidOTP(OTP.length === 6);
    }
  };

  //?======================================
  useEffect(() => {
    changeShowLeaveAuthRoutePopupAuthState(true);
  }, [navigate]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter" && otp.length === 6) {
        verifyBtnRef.current?.click();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [otp]);

  return (
    <article className="z-20 flex w-full flex-col gap-6 bg-white px-8 py-10 xl:w-[75%]">
      <div className="flex flex-col gap-6">
        {/* <DomeCareTitle /> */}
        <div className="text-center">
          <p className="flex flex-col items-center justify-center gap-8 py-8 text-2xl font-bold text-headingTextColor">
            <img
              src={`${BASE_LOCAL_IMAGES_DIR}/${otp_IMG}`}
              alt=""
              className="h-20 w-20"
            />
            {t("verify_your_phone_number")} 🔑
          </p>
          <p className="mx-auto w-3/5 text-base text-textColor">
            {t("code_sent_to")} <span dir="ltr">{displayedPhoneNumber}</span>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center">
        <OtpInput
          value={otp}
          onChange={(OTP: string) => {
            handleOTPInput(OTP);
          }}
          numInputs={6}
          renderSeparator={<span> </span>}
          renderInput={(props) => {
            return (
              <input
                {...props}
                placeholder=""
                className={`m-2 !h-16 !w-16 rounded-xl border bg-inputBg ${
                  isValidOTP
                    ? "border-successMain"
                    : isValidOTP === false
                      ? "border-errorMain"
                      : "border-inputBg"
                }`}
              />
            );
          }}
          containerStyle="flex justify-center items-center"
          shouldAutoFocus={true}
          onPaste={handleVerifyOtpCode}
        />
      </div>

      <p className="w-full text-center text-sm font-normal text-textColor">
        {countdown > 0 ? formatTime(countdown) : ""}
      </p>

      {countdown === 0 && !showMessageOptions && (
        <p
          className="w-full cursor-pointer text-center text-sm font-bold text-primaryMain underline"
          onClick={handleShowMessageOptions}
        >
          {t("send_code_again")}
        </p>
      )}

      <div className="flex flex-col gap-3">
        <Button
          title={VerifyOTPStatus === "pending" ? t("loading") : t("verify")}
          variant="primary"
          extraClassName="text-lg"
          type="submit"
          onClick={handleVerifyOtpCode}
          disabled={VerifyOTPStatus === "pending"}
          ButtonRef={verifyBtnRef}
        />
        {showMessageOptions && (
          <div className="flex flex-col gap-4">
            <div className="flex cursor-pointer items-center overflow-hidden rounded-2xl">
              <img
                src={`${BASE_LOCAL_IMAGES_DIR}/${whatsapp_IMG}`}
                alt="WhatsApp"
                loading="lazy"
                className={`h-full w-16 bg-successMain p-4 ${
                  loadingWhatsApp || loadingSMS || countdown > 0
                    ? "opacity-50"
                    : ""
                }`}
              />
              <Button
                title={loadingWhatsApp ? t("loading") : t("via_whatsapp")}
                extraClassName="bg-successLighter text-successMain rounded-l-none font-semibold !p-5 text-base"
                // disabled={loadingWhatsApp || loadingSMS || countdown > 0}
                onClick={() => handleResendOtpCode(1)}
              />
            </div>
            <div className="flex cursor-pointer items-center overflow-hidden rounded-2xl">
              <img
                src={`${BASE_LOCAL_IMAGES_DIR}/${sms_white_IMG}`}
                alt="SMS"
                loading="lazy"
                className={`h-full w-16 bg-primaryMain p-4 ${
                  loadingWhatsApp || loadingSMS || countdown > 0
                    ? "opacity-50"
                    : ""
                }`}
              />
              <Button
                title={loadingSMS ? t("loading") : t("via_sms")}
                extraClassName="bg-primaryLighter text-primaryMain rounded-l-none font-semibold  !p-5 text-base"
                // disabled={loadingSMS || loadingWhatsApp || countdown > 0}
                onClick={() => handleResendOtpCode(0)}
              />
            </div>
          </div>
        )}
        {/* //!====================Errors==================== */}
        {verifyOtpError ? (
          <ErrorsWrapper message={verifyOtpError?.message} />
        ) : ForgetPasswordCheckCodeError ? (
          <ErrorsWrapper message={ForgetPasswordCheckCodeError?.message} />
        ) : ProfileChangePhoneNumberVerifyError ? (
          <ErrorsWrapper
            message={ProfileChangePhoneNumberVerifyError?.message}
          />
        ) : null}
        {resendOTPError ? (
          <ErrorsWrapper message={resendOTPError?.message} />
        ) : resendForgetPasswordError ? (
          <ErrorsWrapper message={resendForgetPasswordError?.message} />
        ) : ProfileChangePhoneNumberResendOTPCodeError ? (
          <ErrorsWrapper
            message={ProfileChangePhoneNumberResendOTPCodeError?.message}
          />
        ) : null}
      </div>
    </article>
  );
};

export default VerifyOtp;
